export const programGovernanceTour = {
    module: 'program_governance',
    title: 'Program Governance Tour',
    title_vi: 'Hướng dẫn Quản trị chương trình',
    description: 'Connect programme health to assessments, audit readiness, evidence gaps and assigned remediation work.',
    description_vi: 'Liên kết sức khỏe chương trình với đánh giá, mức độ sẵn sàng kiểm toán, khoảng trống tài liệu chứng minh và công việc khắc phục được giao.',
    estimated_duration_seconds: 500,
    revision: '2026-11-04.1',
    steps: [
        {
            step_id: 'explore_program_governance_health',
            navigate_to: '/admin/program-governance/health',
            target: '[data-tour-feature="tour-program-governance-health"]',
            wait_for: '[data-testid="pg-health-page"][data-tour-ready="true"]',
            title: 'Program Health',
            title_vi: 'Sức khỏe chương trình',
            description: 'Use programme health to choose which assessment or evidence gap needs investigation. Latest maturity, readiness and active-assessment measures summarize different processes. Open their registers to review status, ownership and supporting material before planning remediation; an unavailable score is not a passing result.',
            description_vi: 'Dùng sức khỏe chương trình để chọn đánh giá hoặc khoảng trống tài liệu chứng minh cần tìm hiểu. Điểm trưởng thành, mức sẵn sàng và số đánh giá hoạt động tổng hợp các quy trình khác nhau. Mở danh sách để rà soát trạng thái, người phụ trách và tài liệu trước khi lập kế hoạch khắc phục; chưa có điểm không phải kết quả đạt.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'program_health_metrics',
            navigate_to: '/admin/program-governance/health',
            target: '[data-testid="pg-health-page"] .cp-kpi-grid',
            wait_for: '[data-testid="pg-health-page"][data-tour-ready="true"]',
            title: 'Distinguish Maturity, Readiness and Gaps',
            title_vi: 'Phân biệt mức độ trưởng thành, sẵn sàng và khoảng trống',
            description: 'Program Health brings together the latest maturity score, audit readiness, open evidence gaps and active assessments. Read each measure separately: a strong maturity score does not close an evidence gap. A dash indicates that a score is unavailable, not zero or a passing result. Follow the relevant assessment or gap workspace to investigate.',
            description_vi: 'Sức khỏe chương trình tổng hợp điểm trưởng thành gần nhất, mức độ sẵn sàng kiểm toán, khoảng trống tài liệu chứng minh còn mở và các đánh giá đang hoạt động. Đọc riêng từng chỉ số: điểm trưởng thành cao không đóng khoảng trống tài liệu chứng minh. Dấu gạch ngang cho biết chưa có điểm, không phải điểm không hoặc kết quả đạt. Mở không gian đánh giá hoặc khoảng trống tương ứng để tìm hiểu.',
            position: 'bottom',
            duration_ms: 22000,
        },
        {
            step_id: 'explore_program_governance_trends',
            navigate_to: '/admin/program-governance/trends',
            target: '[data-testid="pg-trends-page"] .dm-filters',
            wait_for: '[data-testid="pg-trends-page"][data-tour-ready="true"]',
            title: 'Trend Dashboard',
            title_vi: 'Bảng xu hướng',
            description: 'Choose the metric and period before comparing trend values.',
            description_vi: 'Chọn chỉ số và kỳ trước khi so sánh xu hướng.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_trends_register',
            navigate_to: '/admin/program-governance/trends',
            target: '[data-tour-register="explore_program_governance_trends_register"]',
            wait_for: '[data-testid="pg-trends-page"][data-tour-ready="true"]',
            title: 'Trend Dashboard — record decisions',
            title_vi: 'Bảng xu hướng — quyết định theo bản ghi',
            description: 'A change in metric definition or reporting period changes the comparison’s meaning. Summary and history are separate reads; unavailable history cannot establish a stable trend.',
            description_vi: 'Thay đổi định nghĩa chỉ số hoặc kỳ báo cáo làm thay đổi ý nghĩa so sánh. Tổng hợp và lịch sử tải riêng; lịch sử không truy cập được không xác nhận xu hướng ổn định.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_summaries',
            navigate_to: '/admin/program-governance/summaries',
            target: '[data-testid="pg-summaries-page"] .dm-filters',
            wait_for: '[data-testid="pg-summaries-page"][data-tour-ready="true"]',
            title: 'Executive Summaries',
            title_vi: 'Tóm tắt điều hành',
            description: 'Compare reporting period, version, report type, maturity and readiness figures with the generation date.',
            description_vi: 'So sánh kỳ, phiên bản, loại báo cáo, chỉ số trưởng thành và sẵn sàng cùng ngày tạo.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_summaries_register',
            navigate_to: '/admin/program-governance/summaries',
            target: '[data-tour-register="explore_program_governance_summaries_register"]',
            wait_for: '[data-testid="pg-summaries-page"][data-tour-ready="true"]',
            title: 'Executive Summaries — record decisions',
            title_vi: 'Tóm tắt điều hành — quyết định theo bản ghi',
            description: 'A stored executive summary is a dated report, not live evidence. Open its underlying assessment and gap records before planning remediation.',
            description_vi: 'Tóm tắt điều hành đã lưu là báo cáo theo thời điểm, không phải chứng cứ trực tiếp hiện tại. Mở đánh giá và khoảng trống gốc trước khi lập kế hoạch khắc phục.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_assessments',
            navigate_to: '/admin/program-governance/assessments',
            target: '[data-testid="pg-assessments-page"] .dm-filters',
            wait_for: '[data-testid="pg-assessments-page"][data-tour-ready="true"]',
            title: 'Maturity Assessments',
            title_vi: 'Đánh giá trưởng thành',
            description: 'Compare model, reporting period, status, score and assessor.',
            description_vi: 'So sánh mô hình, kỳ, trạng thái, điểm và người đánh giá.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_assessments_register',
            navigate_to: '/admin/program-governance/assessments',
            target: '[data-tour-register="explore_program_governance_assessments_register"]',
            wait_for: '[data-testid="pg-assessments-page"][data-tour-ready="true"]',
            title: 'Maturity Assessments — record decisions',
            title_vi: 'Đánh giá trưởng thành — quyết định theo bản ghi',
            description: 'Draft or in-progress assessments are not final results, and model availability matters when interpreting a score. The handbook evidence checklist is a separate workspace with different scoring semantics.',
            description_vi: 'Đánh giá nháp hoặc đang làm chưa phải kết quả cuối; cần biết mô hình để diễn giải điểm. Danh sách kiểm tra tài liệu theo sổ tay là không gian riêng với ý nghĩa chấm điểm khác.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_handbook_assessments',
            navigate_to: '/admin/program-governance/handbook-assessments',
            target: '[data-tour-feature="handbook-checklist"]',
            wait_for: '[data-tour-feature="handbook-checklist"][data-tour-ready="true"]',
            title: 'Handbook Assessments',
            title_vi: 'Đánh giá theo cẩm nang',
            description: 'This evidence checklist records responses, supporting evidence and review state; it produces no maturity score. Read assessment status and legal-review status separately before inspecting question evidence. A reviewed or archived checklist is locked; recording evidence and approving review require separate permissions.',
            description_vi: 'Danh sách kiểm tra này ghi câu trả lời, tài liệu chứng minh và trạng thái rà soát; không tạo điểm trưởng thành. Đọc riêng trạng thái đánh giá và rà soát pháp lý trước khi xem tài liệu từng câu hỏi. Danh sách đã rà soát hoặc lưu trữ bị khóa; ghi tài liệu và phê duyệt cần quyền riêng.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_models',
            navigate_to: '/admin/program-governance/models',
            target: '[data-testid="pg-models-page"] .dm-filters',
            wait_for: '[data-testid="pg-models-page"][data-tour-ready="true"]',
            title: 'Maturity Models',
            title_vi: 'Mô hình trưởng thành',
            description: 'Compare model status, version and domain count before choosing an assessment framework.',
            description_vi: 'So sánh trạng thái, phiên bản và số lĩnh vực của mô hình trước khi chọn khung đánh giá.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_models_register',
            navigate_to: '/admin/program-governance/models',
            target: '[data-tour-register="explore_program_governance_models_register"]',
            wait_for: '[data-testid="pg-models-page"][data-tour-ready="true"]',
            title: 'Maturity Models — record decisions',
            title_vi: 'Mô hình trưởng thành — quyết định theo bản ghi',
            description: 'A model defines the assessment structure; it is not itself an assessment result. Changes require model-write access and should be evaluated against existing assessments.',
            description_vi: 'Mô hình xác định cấu trúc, không phải kết quả đánh giá. Thay đổi cần quyền ghi mô hình và phải xem ảnh hưởng tới đánh giá đã có.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_governance_model',
            navigate_to: '/admin/program-governance/governance-model',
            target: '[data-tour-feature="tour-program-governance-governance-model"]',
            wait_for: '[data-testid="pg-governance-model-page"][data-tour-ready="true"]',
            title: 'Governance Model',
            title_vi: 'Mô hình Quản trị',
            description: 'Compare governance type, committee structure and review frequency. The expanded RACI detail, when opened, assigns responsible, accountable, consulted and informed roles. A governance model records responsibility design rather than proving each activity was performed.',
            description_vi: 'So sánh loại quản trị, cơ cấu ủy ban và tần suất rà soát. Khi mở, chi tiết RACI phân công bên thực hiện, chịu trách nhiệm, được tham vấn và được thông báo. Mô hình ghi thiết kế trách nhiệm thay vì chứng minh từng hoạt động đã được thực hiện.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_audit_engagements',
            navigate_to: '/admin/program-governance/audit-engagements',
            target: '[data-tour-feature="tour-program-governance-audit-engagements"]',
            wait_for: '[data-testid="pg-audit-engagements-page"][data-tour-ready="true"]',
            title: 'Audit Engagements',
            title_vi: 'Cuộc kiểm toán',
            description: 'The register is available before any engagement is selected. When empty, it says no engagement matches the view. For recorded engagements, compare title, audited period, auditor and state. Findings belong to a deliberately selected engagement. Unresolved findings, including accepted-but-unfixed ones, prevent closure; viewing the register closes nothing.',
            description_vi: 'Sổ có sẵn trước khi chọn cuộc kiểm toán. Khi trống, sổ cho biết không có cuộc kiểm toán khớp chế độ xem. Với bản ghi hiện có, so sánh tiêu đề, kỳ được kiểm toán, bên kiểm toán và trạng thái. Phát hiện thuộc cuộc kiểm toán được chủ động chọn. Phát hiện chưa xử lý, kể cả đã chấp nhận nhưng chưa sửa, ngăn đóng; xem sổ không đóng cuộc kiểm toán.',
            position: 'bottom',
            duration_ms: 26000,
        },
        {
            step_id: 'explore_program_governance_readiness',
            navigate_to: '/admin/program-governance/readiness',
            target: '[data-tour-feature="audit-readiness-history"]',
            wait_for: '[data-testid="pg-readiness-page"][data-tour-ready="true"]',
            title: 'Audit Readiness',
            title_vi: 'Sẵn sàng kiểm toán',
            description: 'Read overall readiness alongside its domain breakdown and dated assessment history. Latest result and history are fetched separately; unavailable data is not a passing result. Use a weak domain to locate evidence gaps rather than interpreting the score as audit acceptance.',
            description_vi: 'Đọc mức sẵn sàng tổng thể cùng phân tích theo lĩnh vực và lịch sử có ngày. Kết quả mới nhất và lịch sử được tải riêng; dữ liệu không có không phải kết quả đạt. Dùng lĩnh vực yếu để tìm khoảng trống tài liệu thay vì coi điểm là kiểm toán chấp nhận.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_evidence_gaps',
            navigate_to: '/admin/program-governance/evidence-gaps',
            target: '[data-testid="pg-evidence-gaps-page"] .dm-filters',
            wait_for: '[data-testid="pg-evidence-gaps-page"][data-tour-ready="true"]',
            title: 'Evidence Gaps',
            title_vi: 'Khoảng trống bằng chứng',
            description: 'Missing evidence identification.',
            description_vi: 'Xác định chứng cứ bị thiếu.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'program_evidence_gap_triage',
            navigate_to: '/admin/program-governance/evidence-gaps',
            target: '[data-testid="pg-evidence-gaps-page"] [role="search"]',
            wait_for: '[data-testid="pg-evidence-gaps-page"][data-tour-ready="true"]',
            title: 'Triage Evidence Gaps',
            title_vi: 'Phân loại xử lý khoảng trống tài liệu chứng minh',
            description: 'Combine module, status and severity filters to identify the evidence gaps that need attention. Read the assigned owner and target date in the register before planning remediation. Filtering only narrows the view; it does not resolve a gap or confirm audit readiness. Empty results may simply reflect the selected filters.',
            description_vi: 'Kết hợp bộ lọc phân hệ, trạng thái và mức độ nghiêm trọng để xác định khoảng trống tài liệu chứng minh cần xử lý. Đọc người được giao và ngày mục tiêu trong danh sách trước khi lập kế hoạch khắc phục. Lọc chỉ thu hẹp chế độ xem, không giải quyết khoảng trống hoặc xác nhận mức độ sẵn sàng kiểm toán. Không có kết quả có thể chỉ do bộ lọc đã chọn.',
            position: 'bottom',
            duration_ms: 20000,
        },
        {
            step_id: 'explore_program_governance_remediation_plans',
            navigate_to: '/admin/program-governance/remediation-plans',
            target: '[data-tour-feature="tour-program-governance-remediation-plans"]',
            wait_for: '[data-testid="pg-remediation-page"][data-tour-ready="true"]',
            title: 'Remediation Plans',
            title_vi: 'Kế hoạch Khắc phục',
            description: 'Read the plan’s anchor kind and reference with priority, assignee, target date and status. An evidence gap and an audit finding are different anchors; a legacy unknown anchor must remain visibly unknown. Plan completion needs its recorded outcome rather than a changed filter.',
            description_vi: 'Đọc loại và tham chiếu nguồn của kế hoạch cùng ưu tiên, người được giao, hạn và trạng thái. Khoảng trống tài liệu và phát hiện kiểm toán là nguồn khác nhau; nguồn cũ chưa xác định phải giữ rõ là chưa biết. Hoàn thành kế hoạch cần kết quả đã ghi, không phải đổi bộ lọc.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_reporting_periods',
            navigate_to: '/admin/program-governance/reporting-periods',
            target: '[data-testid="pg-reporting-periods-page"] .dm-filters',
            wait_for: '[data-testid="pg-reporting-periods-page"][data-tour-ready="true"]',
            title: 'Reporting Periods',
            title_vi: 'Kỳ báo cáo',
            description: 'Compare reporting-period type, start date, end date and status before reusing a period in assessments or reports.',
            description_vi: 'So sánh loại kỳ, ngày bắt đầu, kết thúc và trạng thái trước khi dùng kỳ trong đánh giá hoặc báo cáo.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_reporting_periods_register',
            navigate_to: '/admin/program-governance/reporting-periods',
            target: '[data-tour-register="explore_program_governance_reporting_periods_register"]',
            wait_for: '[data-testid="pg-reporting-periods-page"][data-tour-ready="true"]',
            title: 'Reporting Periods — record decisions',
            title_vi: 'Kỳ báo cáo — quyết định theo bản ghi',
            description: 'A period defines scope; it does not complete the work within it. Check the intended calendar range before creating or closing a period.',
            description_vi: 'Kỳ xác định phạm vi, không hoàn thành công việc trong kỳ. Kiểm tra khoảng ngày dự kiến trước khi tạo hoặc đóng kỳ.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_obligation_mappings',
            navigate_to: '/admin/program-governance/obligation-mappings',
            target: '[data-tour-feature="tour-program-governance-obligation-mappings"]',
            wait_for: '[data-testid="pg-obligation-mappings-page"][data-tour-ready="true"]',
            title: 'Obligation Mappings',
            title_vi: 'Ánh xạ Nghĩa vụ',
            description: 'Read framework, obligation reference, description, assigned role, department and control links together. RACI activities are a separate responsibility view. A mapping records who should act and which controls relate; it does not prove those controls operated.',
            description_vi: 'Đọc cùng khung, tham chiếu nghĩa vụ, mô tả, vai trò, phòng ban và liên kết kiểm soát. Hoạt động RACI là góc nhìn trách nhiệm riêng. Ánh xạ ghi ai nên làm và kiểm soát liên quan, không chứng minh kiểm soát đã vận hành.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_change_log',
            navigate_to: '/admin/program-governance/change-log',
            target: '[data-tour-feature="tour-program-governance-change-log"]',
            wait_for: '[data-testid="pg-change-log-page"][data-tour-ready="true"]',
            title: 'Change Log',
            title_vi: 'Nhật ký Thay đổi',
            description: 'Read change date, document, type, actor and summary before following a version. A change-log entry records what was reported as changed, while issuance and acknowledgement have their own records. Use the right lifecycle register to establish the current document state.',
            description_vi: 'Đọc ngày, tài liệu, loại, người thực hiện và tóm tắt thay đổi trước khi theo phiên bản. Nhật ký ghi nội dung được báo là thay đổi; ban hành và xác nhận đã đọc có bản ghi riêng. Dùng đúng sổ vòng đời để xác định trạng thái tài liệu hiện tại.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_governance_documents',
            navigate_to: '/admin/program-governance/governance-documents',
            target: '[data-tour-feature="tour-program-governance-governance-documents"]',
            wait_for: '[data-testid="pg-documents-page"][data-tour-ready="true"]',
            title: 'Governance Documents',
            title_vi: 'Tài liệu Quản trị',
            description: 'Compare document title, type, status and version. A registered draft is not an issued policy, and issuance does not imply recipients acknowledged it. Follow drafting, issuance or acknowledgement tracking according to the state you need to investigate.',
            description_vi: 'So sánh tiêu đề, loại, trạng thái và phiên bản tài liệu. Bản nháp đã đăng ký chưa phải chính sách ban hành; ban hành không có nghĩa người nhận đã xác nhận đọc. Theo soạn thảo, ban hành hoặc theo dõi xác nhận tùy trạng thái cần điều tra.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_document_drafting',
            navigate_to: '/admin/program-governance/document-drafting',
            target: '[data-tour-feature="tour-program-governance-document-drafting"]',
            wait_for: '[data-testid="pg-drafting-page"][data-tour-ready="true"]',
            title: 'Document Drafting',
            title_vi: 'Soạn thảo Tài liệu',
            description: 'Compare document type, draft status, drafter and deadline before opening a draft. Drafting records preparation; approval and issuance remain separate lifecycle steps. A deadline is not proof that a document was approved or issued on time.',
            description_vi: 'So sánh loại tài liệu, trạng thái nháp, người soạn và hạn trước khi mở bản nháp. Soạn thảo ghi việc chuẩn bị; phê duyệt và ban hành vẫn là bước riêng. Hạn không chứng minh tài liệu đã được duyệt hoặc ban hành đúng lúc.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_document_issuance',
            navigate_to: '/admin/program-governance/document-issuance',
            target: '[data-tour-feature="tour-program-governance-document-issuance"]',
            wait_for: '[data-testid="pg-issuance-page"][data-tour-ready="true"]',
            title: 'Document Issuance',
            title_vi: 'Ban hành Tài liệu',
            description: 'Read ready-for-issuance documents separately from issued documents. Version identifies the text, while effective date and issuance number identify the recorded issuance. Being ready does not mean issued; the tour neither routes approval nor issues a document.',
            description_vi: 'Đọc tài liệu sẵn sàng ban hành riêng với tài liệu đã ban hành. Phiên bản xác định nội dung; ngày hiệu lực và số ban hành xác định lần ban hành đã ghi. Sẵn sàng chưa có nghĩa đã ban hành; hướng dẫn không chuyển duyệt hoặc ban hành tài liệu.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'governance_issued_document_state',
            navigate_to: '/admin/program-governance/document-issuance',
            target: '[data-tour-issuance="issued"]',
            wait_for: '[data-testid="pg-issuance-page"][data-tour-ready="true"]',
            title: 'Issued version and effective date',
            title_vi: 'Phiên bản đã ban hành và ngày hiệu lực',
            description: 'The issued-document register carries status, version, effective date and issuance number. Use these recorded facts to establish which issuance is being discussed; readiness for issuance and recipient acknowledgement are different lifecycle states.',
            description_vi: 'Sổ tài liệu đã ban hành ghi trạng thái, phiên bản, ngày hiệu lực và số ban hành. Dùng các dữ kiện để xác định lần ban hành đang xem; sẵn sàng ban hành và người nhận xác nhận là các trạng thái vòng đời khác nhau.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_acknowledgment_tracking',
            navigate_to: '/admin/program-governance/acknowledgment-tracking',
            target: '[data-tour-feature="tour-program-governance-acknowledgment-tracking"]',
            wait_for: '[data-testid="pg-acknowledgments-page"][data-tour-ready="true"]',
            title: 'Acknowledgment Tracking',
            title_vi: 'Theo dõi Xác nhận',
            description: 'Compare recipient count, acknowledged count, pending count and percentage for the same document. Issuance is distinct from acknowledgement, and a percentage needs its denominator. Individual recipient details appear when opened; this tour acknowledges nothing.',
            description_vi: 'So sánh số người nhận, đã xác nhận, đang chờ và tỷ lệ cho cùng tài liệu. Ban hành khác với xác nhận đã đọc; tỷ lệ cần được đọc cùng tổng số. Chi tiết người nhận xuất hiện khi mở; hướng dẫn không xác nhận thay ai.',
            position: 'bottom',
            duration_ms: 18000,
        },
        {
            step_id: 'explore_program_governance_review_cadence',
            navigate_to: '/admin/program-governance/review-cadence',
            target: '[data-tour-feature="tour-program-governance-review-cadence"]',
            wait_for: '[data-testid="pg-review-cadence-page"][data-tour-ready="true"]',
            title: 'Review Cadence',
            title_vi: 'Lịch Xem xét',
            description: 'Read document, review frequency, last review, next review and status together. A scheduled next review does not prove the last review happened. Investigate an overdue item in its document context before changing the cadence.',
            description_vi: 'Đọc cùng tài liệu, tần suất, lần rà soát cuối, tiếp theo và trạng thái. Lần rà soát đã lên lịch không chứng minh lần trước đã xảy ra. Xem mục quá hạn trong bối cảnh tài liệu trước khi sửa nhịp rà soát.',
            position: 'bottom',
            duration_ms: 18000,
        },
    ],
};
//# sourceMappingURL=program-governance.js.map